import FeedbackForm from "@/pages/about/components/FeedbackForm";
import SectionHeading from "@/components/feature/SectionHeading";
import { useSeo } from "@/hooks/useSeo";

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 红桃视频在线观看",
    description:
      "了解红桃视频在线观看的品牌定位与服务理念：专注影视内容信息展示，帮助影视爱好者快速搜索、浏览并了解电影、电视剧与综艺资料。",
    keywords: "关于红桃视频,红桃视频在线观看,影视平台介绍,联系我们",
    canonicalPath: "/about",
  });

  const features = [
    {
      icon: "ri-search-eye-line",
      title: "便捷搜索",
      desc: "支持片名、导演、演员与类型关键词模糊匹配，快速定位你想看的作品。",
    },
    {
      icon: "ri-layout-grid-line",
      title: "分类浏览",
      desc: "按电影、电视剧、综艺分类，结合地区、年份与类型多维筛选与排序。",
    },
    {
      icon: "ri-information-2-line",
      title: "资料齐全",
      desc: "每部作品提供评分、类型、导演、主演与剧情简介等完整信息展示。",
    },
    {
      icon: "ri-heart-3-line",
      title: "本地收藏",
      desc: "一键收藏心仪影视，收藏记录保存在你的浏览器本地，随时查看与管理。",
    },
  ];

  const stats = [
    { value: "1,200+", label: "收录作品" },
    { value: "3 大类", label: "内容频道" },
    { value: "每日更新", label: "上新频率" },
    { value: "100%", label: "免费浏览" },
  ];

  return (
    <div className="w-full">
      {/* Banner */}
      <section className="relative h-[300px] w-full overflow-hidden md:h-[380px]">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20abstract%20background%20of%20soft%20red%20and%20amber%20light%20rays%20through%20a%20dark%20theater%20atmosphere%2C%20elegant%20and%20minimal%2C%20high%20detail&width=1600&height=600&seq=rhtv-about-banner&orientation=landscape"
          alt="红桃视频在线观看品牌形象"
          title="关于红桃视频在线观看"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/30" />
        <div className="absolute inset-0 flex items-center px-4 md:px-8">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-400/60 bg-primary-500/20 px-3 py-1 text-xs text-accent-200">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-heart-3-fill text-primary-300" />
              </span>
              关于我们
            </span>
            <h1 className="mt-4 font-heading text-3xl text-background-50 md:text-5xl">
              红桃视频在线观看
            </h1>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
              一个专注影视内容展示的平台，让每一次寻找好作品都变得简单而愉快。
            </p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="w-full px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHeading title="品牌介绍" icon="ri-store-2-line" />
            <div className="space-y-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              <p>
                红桃视频在线观看定位为专业的影视内容展示平台，聚合电影、电视剧、综艺等多样视频资源信息。
                我们的目标用户是广泛的影视爱好者，尤其是希望快速找到最新、热门影视内容并了解详细资料的普通观众。
              </p>
              <p>
                平台采用现代门户与卡片探索相结合的设计，主色调为稳重的品牌红，搭配清晰的白色内容区与分明的信息层级，
                让你在浏览时能迅速定位感兴趣的内容。全站支持响应式布局，无论电脑还是手机，都能获得流畅的浏览体验。
              </p>
              <p>
                需要说明的是，本站为影视信息展示平台，仅提供作品资料查询、分类浏览与搜索服务，
                暂不提供在线播放功能。我们希望通过持续更新与优化，成为你发现好作品的第一站。
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-1">
            {stats.map((s) => (
              <div
                key={s.label}
                className="flex flex-col justify-center rounded-lg border border-background-200 bg-background-100 p-5"
              >
                <span className="font-heading text-2xl text-primary-600 md:text-3xl">{s.value}</span>
                <span className="mt-1 text-sm text-foreground-500">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="w-full bg-background-100 px-4 py-10 md:px-8 md:py-14">
        <SectionHeading
          title="我们提供什么"
          subtitle="围绕影视爱好者的真实需求打造的核心能力"
          icon="ri-service-line"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f) => (
            <div
              key={f.title}
              className="rounded-lg border border-background-200 bg-background-50 p-5 transition-all hover:-translate-y-1 hover:border-primary-300"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                <i className={`${f.icon} text-2xl`} />
              </span>
              <h3 className="mt-4 text-base font-medium text-foreground-950">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-500">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact + form */}
      <section className="w-full px-4 py-10 md:px-8 md:py-14">
        <SectionHeading
          title="联系我们"
          subtitle="有任何建议或合作意向，欢迎随时联系"
          icon="ri-customer-service-2-line"
        />
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                  <i className="ri-mail-line text-xl" />
                </span>
                <div>
                  <div className="text-sm text-foreground-500">联系邮箱</div>
                  <div className="mt-0.5 text-sm font-medium text-foreground-950">
                    contact@hongtaotv.demo
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                  <i className="ri-time-line text-xl" />
                </span>
                <div>
                  <div className="text-sm text-foreground-500">服务时间</div>
                  <div className="mt-0.5 text-sm font-medium text-foreground-950">
                    工作日 9:00 - 18:00
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                  <i className="ri-map-pin-line text-xl" />
                </span>
                <div>
                  <div className="text-sm text-foreground-500">办公地点</div>
                  <div className="mt-0.5 text-sm font-medium text-foreground-950">
                    中国 · 北京
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-2">
            <FeedbackForm />
          </div>
        </div>
      </section>
    </div>
  );
}